import React from 'react';
import PropTypes from 'prop-types';
import '@babel/polyfill'; // needed for stripe to work
import { connect } from 'react-redux';
import {
  withStyles,
  CircularProgress,
  Typography,
  Button,
  Tooltip,
  ListItemText,
  ListItem,
  List,
  Card,
  CardHeader,
  CardContent,
} from '@material-ui/core';
// @material-ui/icons
import CheckIcon from '@material-ui/icons/CheckCircle';
import green from '@material-ui/core/colors/green';
import WarningIcon from '@material-ui/icons/Warning';
import yellow from '@material-ui/core/colors/yellow';
import ErrorIcon from '@material-ui/icons/ErrorOutline';
import red from '@material-ui/core/colors/red';
import InfoIcon from '@material-ui/icons/Info';
import grey from '@material-ui/core/colors/grey';

// stripe
import {
  Elements,
  StripeProvider,
} from 'react-stripe-elements';

// actions
import { showLogsModal } from '@pingum/app-state-management/log/actions';
import {
  updateTenant,
  updateTenantStripCard,
  getTenantCard,
} from '@pingum/app-state-management/tenant/actions';

// pingum components
import AuditLogsButton from '../../../../widgets/AuditLogsButton';
import StripeCardForm from './StripeCardForm';

import styles from './styles';

const threeMonthsMiliseconds = 7776000000;

const notExpiredIcon = (
  <Tooltip title="Not Expired">
    <CheckIcon
      style={{ color: green[400] }}
    />
  </Tooltip>
);
const WarningOnExpiredIcon = (
  <Tooltip title="Card Expires Soon">
    <WarningIcon
      style={{ color: yellow[700] }}
    />
  </Tooltip>
);
const CardErrorIcon = (
  <Tooltip title="Card Expired">
    <ErrorIcon
      style={{ color: red[400] }}
    />
  </Tooltip>
);

export const getExpirationIcon = (year, month) => {
  const expDate = new Date(year, month).getTime();
  const now = new Date().getTime();
  const threeMonthExpirationWarning = new Date(now + threeMonthsMiliseconds).getTime();

  if (expDate < now) {
    // past expiration
    return CardErrorIcon;
  }
  if (expDate < threeMonthExpirationWarning) {
    return WarningOnExpiredIcon;
  }
  // 3 month warning
  return notExpiredIcon;
};

const WarningOnCvcCheck = (
  <Tooltip title="CVC Not Verified Yet - Check Back Soon">
    <WarningIcon
      style={{ color: yellow[700] }}
    />
  </Tooltip>
);
const ErrorOnCvcCheck = (
  <Tooltip title="CVC Check Failed - please re-enter card information">
    <ErrorIcon
      style={{ color: red[400] }}
    />
  </Tooltip>
);
const SuccessOnCvcCheck = (
  <Tooltip title="CVC Check Passed">
    <CheckIcon
      style={{ color: green[400] }}
    />
  </Tooltip>
);

export const getCvcCheckIcon = (status) => {
  switch (status) {
    case 'unchecked':
      // recent card submission
      return WarningOnCvcCheck;
    case 'pass':
      return SuccessOnCvcCheck;
    case 'fail':
      return ErrorOnCvcCheck;
    default:
      return SuccessOnCvcCheck;
  }
};

const WarningOnZipCheck = (
  <Tooltip title="ZIP Code Not Verified Yet - Check Back Soon">
    <WarningIcon
      style={{ color: yellow[700] }}
    />
  </Tooltip>
);
const ErrorOnZipCheck = (
  <Tooltip title="ZIP Code Check Failed - please re-enter card information">
    <ErrorIcon
      style={{ color: red[400] }}
    />
  </Tooltip>
);
const SuccessOnZipCheck = (
  <Tooltip title="ZIP Code Check Passed">
    <CheckIcon
      style={{ color: green[400] }}
    />
  </Tooltip>
);
const NoZipCheck = (
  <Tooltip title="No ZIP Code Required">
    <InfoIcon
      style={{ color: grey[400] }}
    />
  </Tooltip>
);

export const getZipCheckIcon = (status) => {
  switch (status) {
    case 'unchecked':
      // recent card submission
      return WarningOnZipCheck;
    case 'pass':
      return SuccessOnZipCheck;
    case 'fail':
      return ErrorOnZipCheck;
    default:
      return NoZipCheck;
  }
};

export const isCardUnchecked = (zipCheck, cvcCheck) => zipCheck === 'unchecked' || cvcCheck === 'unchecked';

export const isCardValid = (zipCheck, cvcCheck, exYear, exMonth) => {
  const expDate = new Date(exYear, exMonth).getTime();
  const now = new Date().getTime();
  return (zipCheck === 'pass' || zipCheck === 'unavailable' || zipCheck === null)
         && (cvcCheck === 'pass' || cvcCheck === 'unavailable' || cvcCheck === null)
         && (expDate > now);
};

export class PaymenthMethodCardComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = { showStripeCardForm: false };
  }

  showLogsModal = () => {
    this.props.showLogsModal({
      tenantName: this.props.tenant.name,
      service: 'tenant',
      actions: ['Added Payment Method', 'Removed Payment Method'],
    });
  }

  handleSumbitNewPaymentMethod = (token) => {
    this.props.updateTenantStripCard(token.card);
    this.props.updateTenant({
      name: this.props.tenant.name,
      displayName: this.props.tenant.displayName,
      newPaymentPlan: this.state.planIdSubscribingTo,
      stripeAddCardToken: token.id,
    });
    this.showHideStripeCardForm();
    this.checkRefreshOfCardData();
  }

  checkRefreshOfCardData = () => {
    // wait 10 seconds for stripe to process card and reload
    setTimeout(() => {
      if (isCardUnchecked(
        this.props.stripeCard.address_zip_check,
        this.props.stripeCard.cvc_check,
      )) {
        // need to regrab card
        this.props.getTenantCard(this.props.tenant.name, this.props.stripeCard.id);
      }
    }, 10000);
  }

  showHideStripeCardForm = () => {
    this.setState((prevState) => ({ showStripeCardForm: !prevState.showStripeCardForm }));
  }

  render() {
    const { classes } = this.props;
    return (
      <Card>
        <CardHeader
          title="Payment Method"
          subheader="Manage how you will pay for your Pingum subscription"
          action={
            <AuditLogsButton onClick={this.showLogsModal} />
          }
        />
        <CardContent>
          <Typography variant="body2" gutterBottom>
            The payment method on file for your organization will be charged automatically
            at the start of each billing period, or when you upgrade your Pingum subscription
            in the middle of a billing period.
          </Typography>
          {
            this.props.isFetching
            && (
              <div className={classes.loadingContainer}>
                <CircularProgress />
              </div>
            )
          }
          {
            !this.props.isFetching
            && (
              <div>
                {
                (this.props.stripeCard.id && !this.state.showStripeCardForm
                    && !this.props.updateTenantError)
                  ? (
                    <div>
                      <Typography variant="h6" gutterBottom>Card Information</Typography>
                      {
                        (this.props.isLoadingStripeCard || isCardUnchecked(
                          this.props.stripeCard.address_zip_check,
                          this.props.stripeCard.cvc_check,
                        ))
                        && (
                          <div>
                            Validating Card...
                            <CircularProgress
                              id="validating-card-progress"
                              className={this.props.classes.cardProgress}
                              size={25}
                            />
                          </div>
                        )
                      }
                      <List className={classes.root}>
                        <ListItem divider>
                          <CheckIcon
                            style={{ color: green[400] }}
                          />
                          <ListItemText primary="Card Type" secondary={this.props.stripeCard.brand} />
                        </ListItem>
                        <ListItem divider>
                          <CheckIcon
                            style={{ color: green[400] }}
                          />
                          <ListItemText primary="Last 4" secondary={`•••• ${this.props.stripeCard.last4}`} />
                        </ListItem>
                        <ListItem divider>
                          {getExpirationIcon(
                            this.props.stripeCard.exp_year,
                            this.props.stripeCard.exp_month,
                          )}
                          <ListItemText primary="Expiration" secondary={`${this.props.stripeCard.exp_month} / ${this.props.stripeCard.exp_year}`} />
                        </ListItem>
                        <ListItem divider>
                          {getZipCheckIcon(this.props.stripeCard.address_zip_check)}
                          <ListItemText primary="Zip" secondary={this.props.stripeCard.address_zip} />
                        </ListItem>
                        <ListItem divider>
                          {getCvcCheckIcon(this.props.stripeCard.cvc_check)}
                          <ListItemText primary="CVC Check" />
                        </ListItem>
                      </List>
                    </div>
                  )
                  : (
                    <div>
                      {
                      (!this.state.showStripeCardForm && !this.props.updateTenantError)
                      && <Typography variant="h6" gutterBottom id="no-card-message">No card on file</Typography>
                    }
                    </div>
                  )
              }
                { this.props.updateTenantError
                && (
                  <Typography variant="h5" gutterBottom style={{ color: red[400] }} id="error-message">
                    {this.props.updateTenantErrorMessage}
                  </Typography>
                )}
                {
                !this.state.showStripeCardForm
                && (
                  <div className={classes.right}>
                    <Button variant="contained" color="primary" onClick={this.showHideStripeCardForm} className={this.props.classes.editButton}>
                      {this.state.showStripeCardForm
                        ? 'Add'
                        : 'Update'} Card
                    </Button>
                  </div>
                )
              }
                {
                this.state.showStripeCardForm
                  && (
                    <StripeProvider apiKey={env.REACT_APP_STRIPE_PUBLIC_KEY}>
                      <div>
                        <Typography variant="h6" gutterBottom>Change Card Information</Typography>
                        <Elements>
                          <StripeCardForm
                            classes={this.props.classes}
                            stripe={this.state.stripe}
                            showHideStripeCardForm={this.showHideStripeCardForm}
                            handleCardSumbit={this.handleSumbitNewPaymentMethod}
                          />
                        </Elements>
                      </div>
                    </StripeProvider>
                  )
              }

              </div>
            )

          }
        </CardContent>
      </Card>
    );
  }
}

PaymenthMethodCardComponent.propTypes = {
  classes: PropTypes.object,
  tenant: PropTypes.object,
  updateTenant: PropTypes.func,
  updateTenantStripCard: PropTypes.func,
  showLogsModal: PropTypes.func,
  getTenantCard: PropTypes.func,
  isFetching: PropTypes.bool,
  stripeCard: PropTypes.object,
  updateTenantError: PropTypes.bool,
  updateTenantErrorMessage: PropTypes.string,
  isLoadingStripeCard: PropTypes.bool,
};

PaymenthMethodCardComponent.defaultProps = {
  classes: {},
  tenant: {
    name: '',
    displayName: '',
    stripeCardId: null,
  },
  updateTenant: () => {},
  updateTenantStripCard: () => {},
  showLogsModal: () => {},
  getTenantCard: () => {},
  isFetching: true,
  stripeCard: {},
  updateTenantError: false,
  updateTenantErrorMessage: '',
  isLoadingStripeCard: false,
};

export const mapStateToProps = (state) => ({
  isFetching: state.tenant.isFetchingUpdateTenant,
  updateTenantError: state.tenant.updateTenantError,
  updateTenantErrorMessage: state.tenant.updateTenantErrorMessage,
  isLoadingStripeCard: state.tenant.isLoadingStripeCard,
});

export const mapDispatchToProps = (dispatch) => ({
  updateTenant: (request) => dispatch(updateTenant(request)),
  updateTenantStripCard: (stripeCard) => dispatch(updateTenantStripCard(stripeCard)),
  showLogsModal: (request) => dispatch(showLogsModal(request)),
  getTenantCard: (tenantName, cardId) => dispatch(getTenantCard(tenantName, cardId)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(styles, { withTheme: true })(PaymenthMethodCardComponent));
